import type { ComponentProps } from "react";
import type { ButtonProps as RACButtonProps } from "react-aria-components";
import { composeRenderProps, Button as RACButton } from "react-aria-components";
import { Link } from "react-router";
import { tv } from "tailwind-variants";
import { cn } from "~/utils/cn";
import { inputStyles } from "./InputField";
import { Tooltip, TooltipTrigger } from "./Tooltip";

export interface ButtonProps extends RACButtonProps {
  variant?:
    | "primary"
    | "secondary"
    | "destructive"
    | "tertiary"
    | "filter"
    | "table"
    | "quoteForm"
    | "tag"
    | "text"
    | "chart";
  size?: "xs" | "sm" | "md" | "lg";
  isError?: boolean;
}

export const buttonStyles = tv({
  base: "flex items-center justify-center cursor-pointer text-center group/btn",
  variants: {
    variant: {
      primary: "bg-dark-900 text-white hover:bg-dark-600",
      secondary:
        "bg-white border border-outline-200 active:border-dark-300 hover:bg-black/3",
      tertiary: "bg-white text-dark-900 border-outline-200 hover:bg-gray-100",
      table: inputStyles.variants.variant.table,
      quoteForm: inputStyles.variants.variant.quoteForm,
      tag: "bg-white border border-outline-200 active:border-dark-300 hover:bg-black/3 rounded-sm cursor-text h-[1.5rem] py-0 !min-h-0 text-xs px-3 !text-sm",
      text: "text-system-blue hover:brightness-75 !p-0",
      filter: "bg-white text-dark-600 hover:bg-gray-100",
      destructive: "bg-system-red text-white", // TODO: add styling from design system
      chart: "bg-white hover:bg-black/3",
    },
    size: {
      xs: "body-sm-500 rounded-sm px-[0.625rem] h-[1.75rem] gap-[0.375rem] icon-child:size-[0.75rem]",
      sm: "body-md-500 rounded-sm px-[0.75rem] py-[0.25rem] gap-[0.5rem] icon-child:size-[1rem]",
      md: "body-md-500 rounded-md px-[0.875rem] py-[0.5rem] gap-[0.5rem] icon-child:size-[1.125rem]",
      lg: "body-lg-500 rounded-lg px-[1.25rem] py-[0.75rem] gap-[0.75rem] icon-child:size-[1.25rem]",
    },
    isActive: {
      true: "bg-dark-50",
    },
    isDisabled: {
      true: "opacity-50 pointer-events-none",
    },
    isError: {
      true: "border-system-red",
    },
  },
  compoundVariants: [
    {
      variant: "filter",
      class: "rounded-none",
    },
  ],
  defaultVariants: {
    variant: "primary",
    size: "lg",
    isDisabled: false,
  },
});

export function Button(props: ButtonProps) {
  return (
    <RACButton
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        buttonStyles({
          ...renderProps,
          variant: props.variant,
          size: props.size,
          isDisabled: props.isDisabled,
          isError: props.isError,
          className,
        })
      )}
    />
  );
}

interface LinkButtonProps extends ComponentProps<typeof Link> {
  size?: "xs" | "sm" | "md" | "lg";
  variant?:
    | "primary"
    | "secondary"
    | "destructive"
    | "tertiary"
    | "table"
    | "text";
  isDisabled?: boolean;
}

export function LinkButton(props: LinkButtonProps) {
  const { size, variant, isDisabled, ...rest } = props;

  return (
    <Link
      {...rest}
      className={cn(
        buttonStyles({
          variant: variant,
          size: size,
          isDisabled: isDisabled,
        }),
        props.className
      )}
      aria-disabled={isDisabled}
    />
  );
}

const iconButtonStyles = tv({
  base: `flex disabled:opacity-50 items-center shrink-0 justify-center border-[1px] cursor-pointer`,
  variants: {
    variant: {
      outline:
        "border-outline-200 hover:border-dark-300 active:bg-dark-100 text-dark-600",
      solid: "bg-dark-100 border-transparent hover:bg-dark-200",
      white: "bg-white border-white hover:bg-dark-100 text-dark-600",
      black: "bg-dark-900 border-dark-900 hover:bg-dark-600 text-white",
      transparent:
        "border-transparent bg-transparent hover:bg-transparent text-dark-600",
    },
    size: {
      sm: "size-5 rounded-[0.3rem] icon-child:size-3",
      md: "size-[1.5rem] rounded-[0.4rem] icon-child:size-[0.9rem]",
      lg: "size-[2rem] rounded-[0.5rem] icon-child:size-[1rem]",
    },
    isError: {
      true: "border-system-red text-system-red",
    },
  },
  defaultVariants: {
    variant: "outline",
    size: "lg",
  },
});

interface IconButtonProps extends RACButtonProps {
  variant?: "black" | "solid" | "outline" | "white" | "transparent";
  size?: "sm" | "md" | "lg";
  isError?: boolean;
  tooltip?: string;
}

export function IconButton(props: IconButtonProps) {
  const button = (
    <RACButton
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        iconButtonStyles({
          ...renderProps,
          isError: props.isError,
          variant: props.variant,
          size: props.size,
          className,
        })
      )}
    />
  );
  if (props.tooltip) {
    return (
      <TooltipTrigger aria-label={props["aria-label"] ?? props.tooltip}>
        {button}
        <Tooltip>{props.tooltip}</Tooltip>
      </TooltipTrigger>
    );
  } else {
    return button;
  }
}
